import type { ProColumns } from '@ant-design/pro-components'
import { Link } from '@tanstack/react-router'
import { Button, Tag, Typography } from 'antd'
import type { z } from 'zod'
import type { contractDocumentListOutputSchema, ContractDocumentStatus } from '@super-mega/server/contracts/contract'
import { TableRowActions, type TableRowAction } from '@/components/table-row-actions.tsx'
import { CONTRACT_DOCUMENT_STATUS, CONTRACT_TEMPLATE_TYPES } from './const/contract-labels.ts'

export type ContractDocumentRow = z.infer<typeof contractDocumentListOutputSchema>['data'][number]

export function contractDocumentColumns({
  compact,
  canManage,
  canViewContent,
  canEditContent,
  canReadOrders,
  pending,
  getContentHref,
  onStatus,
  onOrder
}: {
  compact: boolean
  canManage: boolean
  canViewContent: boolean
  canEditContent: boolean
  canReadOrders: boolean
  pending: boolean
  getContentHref: (row: ContractDocumentRow) => string
  onStatus: (row: ContractDocumentRow, status?: ContractDocumentStatus) => void
  onOrder: (source: NonNullable<ContractDocumentRow['sourceOrder']>) => void
}): ProColumns<ContractDocumentRow>[] {
  return [
    {
      dataIndex: 'keyword',
      title: '关键词 / ID',
      hideInTable: true,
      fieldProps: { allowClear: true, placeholder: '合同号、合同名称或 ID' }
    },
    { dataIndex: 'id', title: 'ID', copyable: true, search: false, width: compact ? '4rem' : '6rem' },
    {
      dataIndex: 'contractNo',
      title: '合同编号',
      search: false,
      width: '14rem',
      render: (_, row) =>
        row.sourceOrder && canReadOrders ? (
          row.sourceOrder.type === 'rental' ? (
            <Link
              to="/orders/rentals/contract/$rentalId"
              params={{ rentalId: String(row.sourceOrder.id) }}
              search={{ documentId: row.id }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.contractNo}
            </Link>
          ) : (
            <Link
              to="/orders/service/contract/$orderId"
              params={{ orderId: String(row.sourceOrder.id) }}
              search={{ documentId: row.id }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.contractNo}
            </Link>
          )
        ) : canViewContent ? (
          <Link
            to="/contracts/view/$documentId"
            params={{ documentId: String(row.id) }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.contractNo}
          </Link>
        ) : (
          row.contractNo
        )
    },
    {
      dataIndex: 'title',
      title: '合同名称',
      search: false,
      width: '20rem',
      render: (_, row) => (
        <div className="flex flex-col items-start gap-1">
          <Typography.Text ellipsis={{ tooltip: row.title }} className="max-w-[16rem]" strong>
            {row.title}
          </Typography.Text>
          {compact && (
            <Tag className="sm:hidden!" color={CONTRACT_DOCUMENT_STATUS[row.status].color}>
              {CONTRACT_DOCUMENT_STATUS[row.status].label}
            </Tag>
          )}
        </div>
      )
    },
    {
      dataIndex: 'type',
      responsive: compact ? ['md'] : undefined,
      title: '类型',
      width: '8rem',
      render: (_, row) => (
        <Tag color={CONTRACT_TEMPLATE_TYPES[row.type].color}>{CONTRACT_TEMPLATE_TYPES[row.type].label}</Tag>
      ),
      valueEnum: Object.fromEntries(
        Object.entries(CONTRACT_TEMPLATE_TYPES).map(([value, item]) => [value, { text: item.label }])
      )
    },
    {
      dataIndex: 'status',
      responsive: compact ? ['sm'] : undefined,
      title: '状态',
      width: '8rem',
      render: (_, row) => (
        <Tag color={CONTRACT_DOCUMENT_STATUS[row.status].color}>{CONTRACT_DOCUMENT_STATUS[row.status].label}</Tag>
      ),
      valueEnum: Object.fromEntries(
        Object.entries(CONTRACT_DOCUMENT_STATUS).map(([value, item]) => [value, { text: item.label }])
      )
    },
    {
      dataIndex: 'templateName',
      responsive: compact ? ['md'] : undefined,
      title: '来源模板',
      search: false,
      width: '12rem',
      render: (_, row) =>
        row.templateName ? `${row.templateName}${row.templateVersion ? ` · v${row.templateVersion}` : ''}` : '—'
    },
    ...(!compact
      ? [
          {
            key: 'sourceOrder',
            title: '来源订单',
            search: false,
            width: '16rem',
            render: (_: unknown, row: ContractDocumentRow) =>
              row.sourceOrder ? (
                canReadOrders ? (
                  <Button type="link" size="small" className="px-0!" onClick={() => onOrder(row.sourceOrder!)}>
                    {row.sourceOrder.orderNo}
                  </Button>
                ) : (
                  row.sourceOrder.orderNo
                )
              ) : (
                '手工创建'
              )
          }
        ]
      : []),
    {
      dataIndex: 'creator',
      responsive: compact ? ['lg'] : undefined,
      title: '创建人',
      search: false,
      width: '9rem',
      render: (_, row) => row.creator.name
    },
    {
      dataIndex: 'createdAt',
      responsive: compact ? ['lg'] : undefined,
      title: '创建时间',
      valueType: 'dateTime',
      search: false,
      width: '12rem'
    },
    {
      align: 'center',
      key: 'actions',
      title: '操作',
      valueType: 'option',
      fixed: 'right',
      width: '10rem',
      search: false,
      render: (_, row) => {
        const actions: TableRowAction[] = []
        if (canViewContent) {
          actions.push({
            key: canEditContent && row.status === 'confirmed' ? 'content' : 'view',
            label: canEditContent && row.status === 'confirmed' ? '编辑' : '查看',
            disabled: pending,
            href: getContentHref(row),
            target: '_blank',
            rel: 'noopener noreferrer'
          })
        }
        if (canManage) {
          actions.push({ key: 'status', label: '修改状态', disabled: pending, onClick: () => onStatus(row) })
          if (row.status !== 'effective') {
            actions.push({
              key: 'effective',
              label: '生效',
              disabled: pending,
              onClick: () => onStatus(row, 'effective')
            })
          }
          if (row.status !== 'voided') {
            actions.push({
              key: 'voided',
              label: '作废',
              danger: true,
              disabled: pending,
              onClick: () => onStatus(row, 'voided')
            })
          }
        }
        return actions.length ? <TableRowActions resource="contract.document" actions={actions} /> : '—'
      }
    }
  ]
}
